import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AllConversation } from "../api/KnowledgeApiCall";
import {
  KnowledgeCard,
  KnowledgeContentImage,
} from "../api/responsePayload/KnowledgeResponse";
import { showConsoleError } from "../util/ConsoleMessage";
import {
  getQuestionStatusFromSideCardType,
  SideCardType,
} from "../util/QuestionStatus";
import { setPagination } from "./pagination.slice";
import { RootState } from "./common/store";
import { KowledgeContentBulkDelete } from "../api/KnowledgeContentApiCall";

interface ConversationsState {
  items: KnowledgeCard[];
  loading: boolean;
  error?: string;
}

const initialConversationsState: ConversationsState = {
  items: [],
  loading: false,
};

export const fetchConversations = createAsyncThunk(
  "conversations/fetch",
  async (
    args: { endpoint?: string; page?: number } | undefined,
    { getState, dispatch, rejectWithValue }
  ) => {
    try {
      const state: any = getState();
      const statusClicked = state.ui?.statusClicked;
      const currentStatus = getQuestionStatusFromSideCardType(statusClicked);

      const params: Record<string, any> = {
        ...(currentStatus !== null && { status: currentStatus }),
        ...(args?.page != null && { page: args.page }),
      };

      const res = await AllConversation(args?.endpoint, {}, params);
      if (res) {
        dispatch(
          setPagination({
            currentPage: res.current_page,
            nextPageUrl: res.next,
            prevPageUrl: res.previous,
            totalPages: res.total_pages,
            totalCount: res.count,
          })
        );
        return (res.data as KnowledgeCard[]).map((r) => ({
          ...r,
          isSelected: false,
        }));
      }
      return [] as KnowledgeCard[];
    } catch (e: any) {
      showConsoleError("fetchConversations error", e);
      return rejectWithValue(String(e));
    }
  }
);

export const bulkDeleteSelected = createAsyncThunk<
  number[],
  void,
  { state: RootState; rejectValue: string }
>(
  "conversations/bulkDeleteSelected",
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState();
      const status = state.ui.statusClicked;
      const selectedIds = state.conversations.items
        .filter((c) => c.isSelected)
        .map((c) => c.id);

      if (selectedIds.length === 0) return [];
      if (status !== SideCardType.Rejected) return [];

      await KowledgeContentBulkDelete(selectedIds);

      dispatch(removeByIds(selectedIds));
      dispatch(clearSelection());

      const after = getState().conversations.items.length;
      if (after === 0) {
        const page = getState().pagination.currentPage ?? 1;
        dispatch(fetchConversations({ page }));
      }

      return selectedIds;
    } catch (e: any) {
      showConsoleError("bulkDeleteSelected error", e);
      return rejectWithValue(String(e));
    }
  }
);

export const conversationsSlice = createSlice({
  name: "conversations",
  initialState: initialConversationsState,
  reducers: {
    toggleSelected(
      state,
      action: PayloadAction<{ id: number; selected?: boolean }>
    ) {
      const row = state.items.find((i) => i.id === action.payload.id);
      if (row) {
        row.isSelected = action.payload.selected ?? !row.isSelected;
      }
    },
    setAllSelected(state, action: PayloadAction<boolean>) {
      state.items = state.items.map((i) => ({
        ...i,
        isSelected: action.payload,
      }));
    },
    clearSelection(state) {
      state.items = state.items.map((i) => ({ ...i, isSelected: false }));
    },
    removeByIds(state, action: PayloadAction<number[]>) {
      const removeSet = new Set(action.payload);
      state.items = state.items.filter((i) => !removeSet.has(i.id));
    },
    setConversations(state, action: PayloadAction<KnowledgeCard[]>) {
      state.items = action.payload;
    },

    addScreenshotsToConversation(
      state,
      action: PayloadAction<{
        knowledgeId: number;
        images: KnowledgeContentImage[];
      }>
    ) {
      const { knowledgeId, images } = action.payload;
      const row = state.items.find((r) => r.knowledgeId === knowledgeId);
      if (!row) return;
      const prev = row.knowledge_content_image ?? [];
      row.knowledge_content_image = [...prev, ...images];
    },

    removeScreenshotsFromConversation(
      state,
      action: PayloadAction<{ knowledgeId: number; imageIds: number[] }>
    ) {
      const { knowledgeId, imageIds } = action.payload;
      const row = state.items.find((r) => r.knowledgeId === knowledgeId);
      if (!row || !row.knowledge_content_image) return;
      const idSet = new Set(imageIds);
      row.knowledge_content_image = row.knowledge_content_image.filter(
        (img) => !idSet.has(img.image.id)
      );
    },

    setScreenshotsForConversation(
      state,
      action: PayloadAction<{
        knowledgeId: number;
        images: KnowledgeContentImage[];
      }>
    ) {
      const { knowledgeId, images } = action.payload;
      const row = state.items.find((r) => r.knowledgeId === knowledgeId);
      if (!row) return;
      row.knowledge_content_image = images;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConversations.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  toggleSelected,
  setAllSelected,
  clearSelection,
  removeByIds,
  setConversations,
  addScreenshotsToConversation
} = conversationsSlice.actions;
export default conversationsSlice.reducer;
